/**
 * `React.lazy` wrapper that recovers from "stale chunk after deploy".
 *
 * Symptom: after we ship a new build the user has the OLD `index.html`
 * already running in their tab. Vite's content-hashed chunk names
 * mean the old chunk URLs no longer exist on the host. Firebase
 * Hosting rewrites the missing path to index.html (200 OK, text/html),
 * the browser refuses the HTML as a module script, and the lazy
 * import throws with one of:
 *   - "Failed to fetch dynamically imported module"
 *   - "Importing a module script failed"
 *   - "error loading dynamically imported module"
 *
 * Recovery: refresh the page once. The new index.html points at the
 * current hashed chunks, the user's tab catches up. We gate the
 * reload on a sessionStorage marker so a real bug (chunk missing
 * because the deploy is genuinely broken) doesn't ping-pong-reload
 * forever.
 *
 * Use this in `App.tsx` everywhere `React.lazy(() => import(...))`
 * is used.
 */

import { lazy as reactLazy, ComponentType } from "react";

const RELOAD_FLAG = "__upsync_chunk_reload_at__";
const RELOAD_COOLDOWN_MS = 30_000;  // 30s — long enough that a healthy
                                    // page load resets the flag, short
                                    // enough that the next "real" stale
                                    // tab can still self-heal.

function isChunkLoadError(err: unknown): boolean {
  if (!err) return false;
  const msg = (err as any)?.message || String(err);
  return (
    msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("error loading dynamically imported module") ||
    msg.includes("Importing a module script failed") ||
    // Firefox phrasing
    msg.includes("dynamically imported module") ||
    // Chrome MIME-mismatch phrasing
    msg.includes("Failed to load module script")
  );
}

function shouldReload(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_FLAG) || "0");
    return !last || Date.now() - last > RELOAD_COOLDOWN_MS;
  } catch {
    // sessionStorage can throw in iframes / privacy modes — just bail
    // and let the error surface to the user.
    return false;
  }
}

function markReloaded() {
  try {
    sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  } catch { /* ignore */ }
}

export function lazyWithReload<T extends ComponentType<any>>(
  importer: () => Promise<{ default: T }>,
) {
  return reactLazy(async () => {
    try {
      return await importer();
    } catch (err) {
      if (isChunkLoadError(err) && shouldReload()) {
        markReloaded();
        // Hard reload to fetch fresh index.html + new chunk hashes.
        window.location.reload();
        // Block the calling Suspense forever — the reload above is
        // about to navigate away. Throwing here would surface a
        // generic error to the user during the half-second before
        // the reload kicks in.
        return new Promise<never>(() => {});
      }
      throw err;
    }
  });
}


// Safety net: listen for unhandled chunk-load errors that don't go
// through a lazy() wrapper (e.g. router preloads). Same reload logic.
export function installChunkLoadErrorHandler() {
  if (typeof window === "undefined") return;
  const handler = (event: ErrorEvent | PromiseRejectionEvent) => {
    const err = (event as any).reason || (event as any).error || event;
    if (isChunkLoadError(err) && shouldReload()) {
      markReloaded();
      window.location.reload();
    }
  };
  window.addEventListener("error", handler);
  window.addEventListener("unhandledrejection", handler);
}
